/**
 * App entry (T-01). Registers the hand-authored SW manually
 * (`vite.config.ts` sets `injectRegister: null`) and requests persistent
 * storage at install (§10 residual 7) so the outbox survives eviction
 * pressure.
 */

import { render } from "preact";
import { AuthedApp } from "./components/AuthedApp";
import { IosInstallGate } from "./components/IosInstallGate";
import { isIosOutsideStandalone } from "./auth/authenticated-history";

import { initDeviceToken } from "./identity/device-token";
import { watchRegistration } from "./sw/update-channel";
import { isAuthStubEnabled } from "./dev/stub-auth-driver";

// Design integration (Mark-Dev-Handoff-Source-of-Truth.md §6/§10): the real,
// importable tokens first, then the Minimal Dark Mode skin that dresses the
// shell's existing DOM to the 8 locked screen mockups. Tokens MUST load
// before the skin so the --mark-* custom properties resolve.
// Inter is self-hosted (@fontsource) — a field PWA must not depend on a
// font CDN. §6 type scale uses Regular/Medium/Semi Bold/Bold only (400-700).
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "./styles/mark-dark-tokens.css";
import "./styles/mark-skin.css";

if ("serviceWorker" in navigator) {
  // Register + proactively poll for updates, then hand the registration to the
  // update channel, which surfaces an explicit "new version" affordance.
  //
  // This REPLACES an earlier assumption that proved false in the field: that a
  // silently-activated SW would be "picked up cleanly on the next full app
  // launch." Measured 2026-07-27 — an installed PWA resumed from the launcher
  // performs no navigation at all, so the new bundle installs into the precache
  // and is never served; the only escape was clearing app storage. The reason
  // that comment gave for avoiding a reload still stands (a reload during the
  // resume tick mis-laid-out the 100dvh shell), which is exactly why the reload
  // now happens only on a deliberate tap — see src/sw/update-channel.ts.
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { updateViaCache: "none" })
      .then((reg) => {
        watchRegistration(reg);
        const poll = () => reg.update().catch(() => {});
        // Proactively poll for a new SW instead of waiting for the browser's
        // ~24h heuristic — so a fresh deploy lands on next open.
        poll();
        // An installed PWA can remain foregrounded for an entire shift. A deploy
        // must become visible without requiring a lifecycle event or force-close.
        window.setInterval(poll, 5 * 60 * 1000);
        // A mobile "reopen" usually RESUMES the page from memory (no load
        // event fires), so an installed PWA would only ever check for updates
        // on a full cold launch. Re-poll every time the app returns to the
        // foreground so updates land on a plain resume too.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") poll();
        });
      })
      .catch(() => {
        // Offline-first degrade: a failed SW registration falls back to
        // online-only capture rather than blocking the app from rendering.
      });
  });
}

navigator.storage?.persist?.().catch(() => {
  // Best-effort — browsers may deny persistence; the outbox is still IDB
  // durable, just subject to normal eviction heuristics.
});

const rootEl = document.getElementById("app");
if (rootEl) {
  // The production shell is WorkOS-cookie only. Legacy device identity is loaded
  // solely by the explicit auth stub; allowing it into a cookie session lets a
  // stale/shared bearer override the signed-in actor at the coordinator seam.
  const identityReady = isAuthStubEnabled() ? initDeviceToken() : Promise.resolve();
  // iPhone/iPad outside the Home Screen app: the install gate covers the app.
  // The app still mounts underneath so entries saved in this browser can send.
  const iosBrowserTab = isIosOutsideStandalone();
  void identityReady.finally(() =>
    render(
      <>
        <AuthedApp />
        {iosBrowserTab && <IosInstallGate />}
      </>,
      rootEl,
    ),
  );
}
